import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type {
  BinderItem,
  BinderItemsPage,
  BinderItemsRequest,
} from "modules/binder/model/binder";
import {
  binderListsQueryKey,
  binderFacetsQueryKey,
  binderItemsQueryKey,
} from "modules/binder/queryKeys";
import type {
  AutocompleteGameCard,
  FinishEnum,
} from "modules/cards/model/card";
import type { EmbeddedCardPrice } from "modules/cards/model/card-price";
import type {
  CollectionCard,
  FacetCountsResponse,
} from "modules/collections/details/model/collection";
import { collectionCardTransitionKey } from "modules/collections/details/utils/collectionCardTransitionKey";
import {
  applyTotalsDelta,
  optimisticUnitValue,
} from "modules/common/optimisticInventoryTotals";
import {
  rollbackOptimisticCacheSnapshot,
  type OptimisticCacheSnapshot,
} from "modules/common/optimisticCacheSnapshot";
import {
  DEFAULT_USER_PREFERENCES,
  type ShortUser,
} from "modules/common/model/models";
import {
  ItemSortOptionsEnum,
  type TradeItem,
  type TradeListItemModel,
} from "modules/tradeLists/model/tradeList";
import { tradeItemKey } from "modules/tradeLists/utils/tradeItemKey";
import { tradeItemTransitionKey } from "modules/tradeLists/utils/tradeItemTransitionKey";

interface OptimisticBinderAddInput {
  request: BinderItemsRequest;
  card: AutocompleteGameCard;
  quantity: number;
  finishes: FinishEnum[];
  language: CollectionCard["language"];
  condition: CollectionCard["condition"];
  purchasePrice: number;
  anyPrinting: boolean;
  target?: TradeListItemModel;
  user?: ShortUser;
}

interface OptimisticBinderAddHandle {
  newGroup: boolean;
  rollback: () => void;
}

interface OptimisticBinderTradeQuantityAddInput {
  request: BinderItemsRequest;
  item: TradeItem;
}

interface OptimisticBinderTradeQuantityAddHandle {
  applied: boolean;
  commitCreatedIds: (ids: string[]) => void;
  rollback: () => void;
}

interface OptimisticBinderTradeQuantityRemoveHandle {
  applied: boolean;
  rollback: () => void;
}

interface OptimisticBinderTradeQuantityRemoveInput {
  request: BinderItemsRequest;
  item: TradeItem;
  copies: number;
  removedIds?: string[];
}

interface OptimisticBinderCollectionQuantityAddInput {
  request: BinderItemsRequest;
  card: CollectionCard;
}

interface OptimisticBinderCollectionQuantityAddHandle {
  applied: boolean;
  commitCreatedId: (id?: string) => void;
  rollback: () => void;
}

interface OptimisticBinderCollectionQuantityRemoveInput {
  request: BinderItemsRequest;
  card: CollectionCard;
  removedIds: string[];
}

interface OptimisticBinderCollectionQuantityRemoveHandle {
  applied: boolean;
  rollback: () => void;
}

const OPTIMISTIC_ID_PREFIX = "binder-optimistic:";

function sameFinishes(
  left: readonly FinishEnum[],
  right: readonly FinishEnum[]
) {
  if (left.length !== right.length) return false;
  const leftSorted = [...left].sort();
  const rightSorted = [...right].sort();
  return leftSorted.every((finish, index) => finish === rightSorted[index]);
}

function embeddedPrice(card: AutocompleteGameCard): EmbeddedCardPrice {
  return {
    value: {
      default: card.price.default,
      nonfoil: card.price.nonfoil,
      foil: card.price.foil,
      etched: card.price.etched,
      metal: card.price.metal,
      metalDeluxe: card.price.metalDeluxe,
      trendDefault: card.price.default,
      trendNonfoil: card.price.nonfoil,
      trendFoil: card.price.foil,
      trendEtched: card.price.etched,
      trendMetal: card.price.metal,
      trendMetalDeluxe: card.price.metalDeluxe,
    },
    timestamp: new Date().toISOString(),
    currency: card.price.currency,
    deltas: {},
  };
}

function collectionGroupMatches(
  item: BinderItem,
  input: OptimisticBinderAddInput
) {
  if (item.kind !== "collection") return false;
  return (
    item.card.dataId === input.card.id &&
    item.card.condition === input.condition &&
    item.card.language === input.language &&
    (item.card.purchasePrice ?? 0) === input.purchasePrice &&
    !item.card.tradeItem &&
    sameFinishes(item.card.finishes, input.finishes)
  );
}

function tradeGroupMatches(item: BinderItem, input: OptimisticBinderAddInput) {
  if (item.kind !== "list") return false;
  return (
    item.item.list.id === input.target?.id &&
    item.item.dataId === input.card.id &&
    item.item.condition === input.condition &&
    item.item.language === input.language &&
    item.item.anyPrinting === input.anyPrinting &&
    sameFinishes(item.item.finishes, input.finishes)
  );
}

function optimisticCollectionItem(
  input: OptimisticBinderAddInput,
  ids: string[],
  now: string
): BinderItem {
  return {
    kind: "collection",
    card: {
      game: input.card.game,
      finishes: input.finishes,
      language: input.language,
      condition: input.condition,
      dataId: input.card.id,
      setCode: input.card.setCode,
      name: input.card.name,
      ids,
      quantity: input.quantity,
      updatedAt: now,
      createdAt: now,
      price: embeddedPrice(input.card),
      purchasePrice: input.purchasePrice,
      types: [],
      rarity: "",
      costPrinted: input.card.costPrinted,
      costValue: 0,
      number: input.card.number,
    },
  };
}

function optimisticTradeItem(
  input: OptimisticBinderAddInput,
  ids: string[],
  now: string
): BinderItem {
  const target = input.target!;
  const user = input.user ?? {
    id: target.userId,
    username: "",
    email: "",
    preferredAvatar: "",
    preferences: DEFAULT_USER_PREFERENCES,
  };
  const item: TradeItem = {
    dataId: input.card.id,
    oracleId: input.card.id,
    createdAt: now,
    updatedAt: now,
    ids,
    quantity: String(input.quantity),
    list: { id: target.id, name: target.name, type: target.type },
    finishes: input.finishes,
    language: input.language,
    condition: input.condition,
    user,
    anyPrinting: input.anyPrinting,
    price: embeddedPrice(input.card),
    game: input.card.game,
    name: input.card.name,
    setCode: input.card.setCode,
    types: [],
    rarity: "",
    costPrinted: input.card.costPrinted,
    costValue: 0,
    number: input.card.number,
  };
  return { kind: "list", item };
}

function itemValue(item: BinderItem, sort: ItemSortOptionsEnum) {
  const card = item.kind === "collection" ? item.card : item.item;
  switch (sort) {
    case ItemSortOptionsEnum.Name:
      return card.name.toLocaleLowerCase();
    case ItemSortOptionsEnum.Price:
      return optimisticUnitValue(card.price);
    case ItemSortOptionsEnum.Quantity:
      return Number(card.quantity);
    case ItemSortOptionsEnum.CreatedAt:
      return card.createdAt;
    case ItemSortOptionsEnum.UpdatedAt:
      return card.updatedAt;
  }
}

function sortItems(items: BinderItem[], request: BinderItemsRequest) {
  const direction = request.order === "desc" ? -1 : 1;
  return items.sort((left, right) => {
    const leftValue = itemValue(left, request.sort);
    const rightValue = itemValue(right, request.sort);
    if (leftValue < rightValue) return -1 * direction;
    if (leftValue > rightValue) return direction;
    return 0;
  });
}

function replacePages(
  current: InfiniteData<BinderItemsPage> | undefined,
  items: BinderItem[]
): InfiniteData<BinderItemsPage> {
  if (!current) {
    return {
      pages: [{ items, hasMore: false }],
      pageParams: [1],
    };
  }

  let offset = 0;
  return {
    ...current,
    pages: current.pages.map((page, index) => {
      const isLast = index === current.pages.length - 1;
      const size = isLast ? items.length - offset : page.items.length;
      const next = {
        ...page,
        items: items.slice(offset, offset + size),
      };
      offset += size;
      return next;
    }),
  };
}

function removeOptimisticIds(item: BinderItem, ids: ReadonlySet<string>) {
  if (item.kind === "collection") {
    const remaining = item.card.ids.filter((id) => !ids.has(id));
    return remaining.length === 0
      ? undefined
      : {
          ...item,
          card: { ...item.card, ids: remaining, quantity: remaining.length },
        };
  }

  const remaining = item.item.ids.filter((id) => !ids.has(id));
  return remaining.length === 0
    ? undefined
    : {
        ...item,
        item: {
          ...item.item,
          ids: remaining,
          quantity: String(remaining.length),
        },
      };
}

export function applyOptimisticBinderAdd(
  queryClient: QueryClient,
  input: OptimisticBinderAddInput
): OptimisticBinderAddHandle {
  const itemKey = binderItemsQueryKey(input.request);
  const facetKey = binderFacetsQueryKey(input.request);
  const listKey = input.target
    ? binderListsQueryKey(input.target.userId)
    : undefined;
  // A card cannot safely be projected into a filtered result without running
  // the server's complete facet contract. Likewise, adding to list B while
  // viewing list A must not contaminate A's cached rows or totals. The list
  // rail count remains optimistic in both cases.
  const patchActiveView =
    input.request.query.trim() === "" &&
    Object.keys(input.request.facets).length === 0 &&
    (input.request.section === "collection" ||
      !input.request.listId ||
      input.request.listId === input.target?.id);
  const optimisticIds = Array.from(
    { length: input.quantity },
    () => `${OPTIMISTIC_ID_PREFIX}${crypto.randomUUID()}`
  );
  const optimisticIdSet = new Set(optimisticIds);
  const current =
    queryClient.getQueryData<InfiniteData<BinderItemsPage>>(itemKey);
  const flat = current?.pages.flatMap((page) => page.items) ?? [];
  const matches =
    input.request.section === "collection"
      ? (item: BinderItem) => collectionGroupMatches(item, input)
      : (item: BinderItem) => tradeGroupMatches(item, input);
  const matchIndex = flat.findIndex(matches);
  const now = new Date().toISOString();
  const nextItems = [...flat];

  if (patchActiveView && matchIndex >= 0) {
    const existing = nextItems[matchIndex];
    nextItems[matchIndex] =
      existing.kind === "collection"
        ? {
            ...existing,
            card: {
              ...existing.card,
              ids: [...existing.card.ids, ...optimisticIds],
              quantity: existing.card.quantity + input.quantity,
              updatedAt: now,
            },
          }
        : {
            ...existing,
            item: {
              ...existing.item,
              ids: [...existing.item.ids, ...optimisticIds],
              quantity: String(Number(existing.item.quantity) + input.quantity),
              updatedAt: now,
            },
          };
  } else if (patchActiveView) {
    nextItems.push(
      input.request.section === "collection"
        ? optimisticCollectionItem(input, optimisticIds, now)
        : optimisticTradeItem(input, optimisticIds, now)
    );
  }

  if (patchActiveView) {
    queryClient.setQueryData(
      itemKey,
      replacePages(current, sortItems(nextItems, input.request))
    );
  }

  const delta = {
    copies: input.quantity,
    value: optimisticUnitValue(input.card.price) * input.quantity,
    newGroups: patchActiveView && matchIndex < 0 ? 1 : 0,
  };
  if (patchActiveView) {
    queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
      value ? applyTotalsDelta(value, delta) : value
    );
  }

  if (input.target && listKey) {
    queryClient.setQueryData<TradeListItemModel[]>(listKey, (lists = []) =>
      lists.map((list) =>
        list.id === input.target!.id
          ? { ...list, itemsCount: list.itemsCount + input.quantity }
          : list
      )
    );
  }

  return {
    newGroup: patchActiveView && matchIndex < 0,
    rollback: () => {
      if (patchActiveView) {
        queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
          itemKey,
          (value) => {
            if (!value) return value;
            const remaining = value.pages
              .flatMap((page) => page.items)
              .map((item) => removeOptimisticIds(item, optimisticIdSet))
              .filter((item): item is BinderItem => item !== undefined);
            return replacePages(value, sortItems(remaining, input.request));
          }
        );
        queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
          value
            ? applyTotalsDelta(value, {
                copies: -delta.copies,
                value: -delta.value,
                newGroups: -delta.newGroups,
              })
            : value
        );
      }
      if (input.target && listKey) {
        queryClient.setQueryData<TradeListItemModel[]>(listKey, (lists = []) =>
          lists.map((list) =>
            list.id === input.target!.id
              ? {
                  ...list,
                  itemsCount: Math.max(0, list.itemsCount - input.quantity),
                }
              : list
          )
        );
      }
      if (patchActiveView) {
        void queryClient.invalidateQueries({ queryKey: itemKey, exact: true });
        void queryClient.invalidateQueries({ queryKey: facetKey, exact: true });
      }
    },
  };
}

/**
 * Adds one owned copy to the Collection view without waiting for the browse
 * endpoint. Collection creates are intentionally unlinked, so clicking Add one
 * on a linked row updates (or creates) its unlinked sibling group.
 */
export async function applyOptimisticBinderCollectionQuantityAdd(
  queryClient: QueryClient,
  input: OptimisticBinderCollectionQuantityAddInput
): Promise<OptimisticBinderCollectionQuantityAddHandle> {
  const itemKey = binderItemsQueryKey(input.request);
  const facetKey = binderFacetsQueryKey(input.request);
  await Promise.all([
    queryClient.cancelQueries({ queryKey: itemKey, exact: true }),
    queryClient.cancelQueries({ queryKey: facetKey, exact: true }),
  ]);

  const current =
    queryClient.getQueryData<InfiniteData<BinderItemsPage>>(itemKey);
  if (!current || input.request.section !== "collection") {
    return {
      applied: false,
      commitCreatedId: () => {},
      rollback: () => {},
    };
  }

  const targetTransitionKey = collectionCardTransitionKey({
    ...input.card,
    tradeItem: undefined,
  });
  const flat = current.pages.flatMap((page) => page.items);
  const matchIndex = flat.findIndex(
    (entry) =>
      entry.kind === "collection" &&
      collectionCardTransitionKey(entry.card) === targetTransitionKey
  );
  const canCreateVisibleGroup =
    input.request.query.trim() === "" &&
    Object.keys(input.request.facets).length === 0;
  if (matchIndex < 0 && !canCreateVisibleGroup) {
    return {
      applied: false,
      commitCreatedId: () => {},
      rollback: () => {},
    };
  }
  const now = new Date().toISOString();
  const nextItems = [...flat];

  if (matchIndex >= 0) {
    const existing = nextItems[matchIndex];
    if (existing.kind !== "collection") {
      return {
        applied: false,
        commitCreatedId: () => {},
        rollback: () => {},
      };
    }
    nextItems[matchIndex] = {
      ...existing,
      card: {
        ...existing.card,
        quantity: existing.card.quantity + 1,
        updatedAt: now,
      },
    };
  } else {
    nextItems.push({
      kind: "collection",
      card: {
        ...input.card,
        ids: [],
        availableIds: undefined,
        quantity: 1,
        tradeItem: undefined,
        spokenForQuantity: 0,
        activeTrades: undefined,
        createdAt: now,
        updatedAt: now,
      },
    });
  }

  const optimisticItems = queryClient.setQueryData<
    InfiniteData<BinderItemsPage>
  >(itemKey, replacePages(current, sortItems(nextItems, input.request)));
  if (!optimisticItems) {
    return {
      applied: false,
      commitCreatedId: () => {},
      rollback: () => {},
    };
  }
  const itemSnapshot: OptimisticCacheSnapshot<InfiniteData<BinderItemsPage>> = {
    previous: current,
    optimistic: optimisticItems,
  };

  const delta = {
    copies: 1,
    value: optimisticUnitValue(input.card.price),
    newGroups: matchIndex < 0 ? 1 : 0,
  };
  const previousFacets =
    queryClient.getQueryData<FacetCountsResponse>(facetKey);
  const nextFacets = previousFacets
    ? applyTotalsDelta(previousFacets, delta)
    : undefined;
  const optimisticFacets =
    nextFacets && nextFacets !== previousFacets
      ? queryClient.setQueryData<FacetCountsResponse>(facetKey, nextFacets)
      : undefined;
  const facetSnapshot =
    previousFacets && optimisticFacets
      ? ({
          previous: previousFacets,
          optimistic: optimisticFacets,
        } satisfies OptimisticCacheSnapshot<FacetCountsResponse>)
      : undefined;

  let rolledBack = false;
  return {
    applied: true,
    commitCreatedId: (id) => {
      if (!id) return;
      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => {
          if (!value) return value;
          let changed = false;
          const pages = value.pages.map((page) => ({
            ...page,
            items: page.items.map((entry) => {
              if (
                entry.kind !== "collection" ||
                collectionCardTransitionKey(entry.card) !==
                  targetTransitionKey ||
                entry.card.ids.includes(id) ||
                entry.card.ids.length >= entry.card.quantity
              ) {
                return entry;
              }
              changed = true;
              return {
                ...entry,
                card: {
                  ...entry.card,
                  ids: [...entry.card.ids, id],
                  availableIds:
                    entry.card.availableIds === undefined
                      ? undefined
                      : [...entry.card.availableIds, id],
                },
              };
            }),
          }));
          return changed ? { ...value, pages } : value;
        }
      );
    },
    rollback: () => {
      if (rolledBack) return;
      rolledBack = true;
      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => rollbackOptimisticCacheSnapshot(value, itemSnapshot)
      );
      if (facetSnapshot) {
        queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
          rollbackOptimisticCacheSnapshot(value, facetSnapshot)
        );
      }
    },
  };
}

/** Removes exact free Collection copies and immediately re-sorts the row. */
export async function applyOptimisticBinderCollectionQuantityRemove(
  queryClient: QueryClient,
  input: OptimisticBinderCollectionQuantityRemoveInput
): Promise<OptimisticBinderCollectionQuantityRemoveHandle> {
  const itemKey = binderItemsQueryKey(input.request);
  const facetKey = binderFacetsQueryKey(input.request);
  await Promise.all([
    queryClient.cancelQueries({ queryKey: itemKey, exact: true }),
    queryClient.cancelQueries({ queryKey: facetKey, exact: true }),
  ]);

  const removedIds = [...new Set(input.removedIds.filter(Boolean))];
  if (removedIds.length === 0) {
    return { applied: false, rollback: () => {} };
  }

  const current =
    queryClient.getQueryData<InfiniteData<BinderItemsPage>>(itemKey);
  const flat = current?.pages.flatMap((page) => page.items) ?? [];
  const transitionKey = collectionCardTransitionKey(input.card);
  const matchIndex = flat.findIndex(
    (entry) =>
      entry.kind === "collection" &&
      collectionCardTransitionKey(entry.card) === transitionKey &&
      removedIds.every((id) => entry.card.ids.includes(id))
  );
  const existing = matchIndex >= 0 ? flat[matchIndex] : undefined;
  if (!current || existing?.kind !== "collection") {
    return { applied: false, rollback: () => {} };
  }
  if (
    existing.card.quantity !== existing.card.ids.length ||
    removedIds.length > existing.card.quantity
  ) {
    return { applied: false, rollback: () => {} };
  }

  const removedIdSet = new Set(removedIds);
  const remainingIds = existing.card.ids.filter((id) => !removedIdSet.has(id));
  const nextItems = [...flat];
  if (remainingIds.length === 0) {
    nextItems.splice(matchIndex, 1);
  } else {
    nextItems[matchIndex] = {
      ...existing,
      card: {
        ...existing.card,
        ids: remainingIds,
        availableIds:
          existing.card.availableIds === undefined
            ? undefined
            : existing.card.availableIds.filter((id) => !removedIdSet.has(id)),
        quantity: remainingIds.length,
        updatedAt: new Date().toISOString(),
      },
    };
  }

  const optimisticItems = queryClient.setQueryData<
    InfiniteData<BinderItemsPage>
  >(itemKey, replacePages(current, sortItems(nextItems, input.request)));
  if (!optimisticItems) {
    return { applied: false, rollback: () => {} };
  }
  const itemSnapshot: OptimisticCacheSnapshot<InfiniteData<BinderItemsPage>> = {
    previous: current,
    optimistic: optimisticItems,
  };

  const delta = {
    copies: -removedIds.length,
    value:
      -optimisticUnitValue(input.card.price) * Math.max(1, removedIds.length),
    newGroups: remainingIds.length === 0 ? -1 : 0,
  };
  const previousFacets =
    queryClient.getQueryData<FacetCountsResponse>(facetKey);
  const nextFacets = previousFacets
    ? applyTotalsDelta(previousFacets, delta)
    : undefined;
  const optimisticFacets =
    nextFacets && nextFacets !== previousFacets
      ? queryClient.setQueryData<FacetCountsResponse>(facetKey, nextFacets)
      : undefined;
  const facetSnapshot =
    previousFacets && optimisticFacets
      ? ({
          previous: previousFacets,
          optimistic: optimisticFacets,
        } satisfies OptimisticCacheSnapshot<FacetCountsResponse>)
      : undefined;

  let rolledBack = false;
  return {
    applied: true,
    rollback: () => {
      if (rolledBack) return;
      rolledBack = true;
      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => rollbackOptimisticCacheSnapshot(value, itemSnapshot)
      );
      if (facetSnapshot) {
        queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
          rollbackOptimisticCacheSnapshot(value, facetSnapshot)
        );
      }
    },
  };
}

/**
 * Optimistically projects one newly-created copy into an existing Binder row.
 *
 * The real group IDs stay unchanged only while the request is in flight. The
 * add endpoint returns the committed copy IDs, which are appended through the
 * handle below before any later copy-level action can use the row.
 */
export async function applyOptimisticBinderTradeQuantityAdd(
  queryClient: QueryClient,
  input: OptimisticBinderTradeQuantityAddInput
): Promise<OptimisticBinderTradeQuantityAddHandle> {
  const itemKey = binderItemsQueryKey(input.request);
  const facetKey = binderFacetsQueryKey(input.request);
  const listKey = binderListsQueryKey(input.item.user.id);
  await Promise.all([
    queryClient.cancelQueries({ queryKey: itemKey, exact: true }),
    queryClient.cancelQueries({ queryKey: facetKey, exact: true }),
    queryClient.cancelQueries({ queryKey: listKey, exact: true }),
  ]);

  const groupKey = tradeItemKey(input.item);
  const transitionKey = tradeItemTransitionKey(input.item);
  const current =
    queryClient.getQueryData<InfiniteData<BinderItemsPage>>(itemKey);
  const flat = current?.pages.flatMap((page) => page.items) ?? [];
  const matchIndex = flat.findIndex(
    (entry) =>
      entry.kind === "list" &&
      entry.item.list.id === input.item.list.id &&
      tradeItemKey(entry.item) === groupKey
  );
  const existing = matchIndex >= 0 ? flat[matchIndex] : undefined;

  if (!current || existing?.kind !== "list") {
    return {
      applied: false,
      commitCreatedIds: () => {},
      rollback: () => {},
    };
  }

  const previousQuantity = Number(existing.item.quantity);
  if (!Number.isFinite(previousQuantity) || previousQuantity < 1) {
    return {
      applied: false,
      commitCreatedIds: () => {},
      rollback: () => {},
    };
  }

  const optimisticUpdatedAt = new Date().toISOString();
  const nextItems = [...flat];
  nextItems[matchIndex] = {
    ...existing,
    item: {
      ...existing.item,
      quantity: String(previousQuantity + 1),
      updatedAt: optimisticUpdatedAt,
    },
  };
  const optimisticItems = queryClient.setQueryData<
    InfiniteData<BinderItemsPage>
  >(itemKey, replacePages(current, sortItems(nextItems, input.request)));
  if (!optimisticItems) {
    return {
      applied: false,
      commitCreatedIds: () => {},
      rollback: () => {},
    };
  }
  const itemSnapshot: OptimisticCacheSnapshot<InfiniteData<BinderItemsPage>> = {
    previous: current,
    optimistic: optimisticItems,
  };

  const delta = {
    copies: 1,
    value: optimisticUnitValue(input.item.price),
    newGroups: 0,
  };
  const previousFacets =
    queryClient.getQueryData<FacetCountsResponse>(facetKey);
  const nextFacets = previousFacets
    ? applyTotalsDelta(previousFacets, delta)
    : undefined;
  const optimisticFacets =
    nextFacets && nextFacets !== previousFacets
      ? queryClient.setQueryData<FacetCountsResponse>(facetKey, nextFacets)
      : undefined;
  const facetSnapshot =
    previousFacets && optimisticFacets
      ? ({
          previous: previousFacets,
          optimistic: optimisticFacets,
        } satisfies OptimisticCacheSnapshot<FacetCountsResponse>)
      : undefined;

  const previousLists = queryClient.getQueryData<TradeListItemModel[]>(listKey);
  const targetListIndex =
    previousLists?.findIndex((list) => list.id === input.item.list.id) ?? -1;
  const nextLists = previousLists ? [...previousLists] : undefined;
  if (nextLists && targetListIndex >= 0) {
    const list = nextLists[targetListIndex];
    nextLists[targetListIndex] = { ...list, itemsCount: list.itemsCount + 1 };
  }
  const optimisticLists =
    previousLists && nextLists && targetListIndex >= 0
      ? queryClient.setQueryData<TradeListItemModel[]>(listKey, nextLists)
      : undefined;
  const listSnapshot =
    previousLists && optimisticLists
      ? ({
          previous: previousLists,
          optimistic: optimisticLists,
        } satisfies OptimisticCacheSnapshot<TradeListItemModel[]>)
      : undefined;

  let rolledBack = false;
  return {
    applied: true,
    commitCreatedIds: (ids) => {
      const createdIds = [...new Set(ids.filter(Boolean))];
      if (createdIds.length === 0) return;

      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => {
          if (!value) return value;
          let changed = false;
          const pages = value.pages.map((page) => ({
            ...page,
            items: page.items.map((entry) => {
              if (
                entry.kind !== "list" ||
                entry.item.list.id !== input.item.list.id ||
                tradeItemTransitionKey(entry.item) !== transitionKey
              ) {
                return entry;
              }

              const knownIds = new Set(entry.item.ids);
              const appendedIds = createdIds.filter((id) => !knownIds.has(id));
              if (appendedIds.length === 0) return entry;
              changed = true;
              return {
                ...entry,
                item: {
                  ...entry.item,
                  ids: [...entry.item.ids, ...appendedIds],
                  availableIds:
                    entry.item.availableIds === undefined
                      ? undefined
                      : [...entry.item.availableIds, ...appendedIds],
                },
              };
            }),
          }));
          return changed ? { ...value, pages } : value;
        }
      );
    },
    rollback: () => {
      if (rolledBack) return;
      rolledBack = true;

      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => rollbackOptimisticCacheSnapshot(value, itemSnapshot)
      );
      if (facetSnapshot) {
        queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
          rollbackOptimisticCacheSnapshot(value, facetSnapshot)
        );
      }
      if (listSnapshot) {
        queryClient.setQueryData<TradeListItemModel[]>(listKey, (lists) =>
          rollbackOptimisticCacheSnapshot(lists, listSnapshot)
        );
      }
      // The owning mutation batch performs one trailing reconciliation. An
      // eager refetch here could overwrite another card's newer optimism.
    },
  };
}

/**
 * Optimistically removes one or every copy from a Binder trade row.
 *
 * A one-copy removal receives the exact unreserved ID sent to the delete
 * endpoint and removes it from the optimistic row immediately. Bulk/group
 * removals can still use the count-only path because the row disappears.
 */
export async function applyOptimisticBinderTradeQuantityRemove(
  queryClient: QueryClient,
  input: OptimisticBinderTradeQuantityRemoveInput
): Promise<OptimisticBinderTradeQuantityRemoveHandle> {
  const itemKey = binderItemsQueryKey(input.request);
  const facetKey = binderFacetsQueryKey(input.request);
  const listKey = binderListsQueryKey(input.item.user.id);
  await Promise.all([
    queryClient.cancelQueries({ queryKey: itemKey, exact: true }),
    queryClient.cancelQueries({ queryKey: facetKey, exact: true }),
    queryClient.cancelQueries({ queryKey: listKey, exact: true }),
  ]);

  const removedIds = input.removedIds
    ? [...new Set(input.removedIds.filter(Boolean))]
    : undefined;
  const copies = removedIds
    ? removedIds.length
    : Math.min(Math.max(0, Math.trunc(input.copies)), input.item.ids.length);
  if (copies === 0) return { applied: false, rollback: () => {} };

  const groupKey = tradeItemKey(input.item);
  const current =
    queryClient.getQueryData<InfiniteData<BinderItemsPage>>(itemKey);
  const flat = current?.pages.flatMap((page) => page.items) ?? [];
  const matchIndex = flat.findIndex(
    (entry) =>
      entry.kind === "list" &&
      entry.item.list.id === input.item.list.id &&
      tradeItemKey(entry.item) === groupKey
  );
  const existing = matchIndex >= 0 ? flat[matchIndex] : undefined;

  if (!current || existing?.kind !== "list") {
    return { applied: false, rollback: () => {} };
  }

  const previousQuantity = Number(existing.item.quantity);
  if (
    !Number.isFinite(previousQuantity) ||
    previousQuantity !== existing.item.ids.length ||
    copies > previousQuantity ||
    (removedIds !== undefined &&
      removedIds.some((id) => !existing.item.ids.includes(id)))
  ) {
    return { applied: false, rollback: () => {} };
  }

  const remainingQuantity = previousQuantity - copies;
  const removedIdSet = new Set(removedIds);
  const remainingIds = removedIds
    ? existing.item.ids.filter((id) => !removedIdSet.has(id))
    : existing.item.ids;
  const nextItems = [...flat];
  if (remainingQuantity === 0) {
    nextItems.splice(matchIndex, 1);
  } else {
    nextItems[matchIndex] = {
      ...existing,
      item: {
        ...existing.item,
        ids: remainingIds,
        availableIds:
          existing.item.availableIds === undefined || !removedIds
            ? existing.item.availableIds
            : existing.item.availableIds.filter((id) => !removedIdSet.has(id)),
        quantity: String(remainingQuantity),
        updatedAt: new Date().toISOString(),
      },
    };
  }

  const optimisticItems = queryClient.setQueryData<
    InfiniteData<BinderItemsPage>
  >(itemKey, replacePages(current, sortItems(nextItems, input.request)));
  if (!optimisticItems) {
    return { applied: false, rollback: () => {} };
  }
  const itemSnapshot: OptimisticCacheSnapshot<InfiniteData<BinderItemsPage>> = {
    previous: current,
    optimistic: optimisticItems,
  };

  const delta = {
    copies: -copies,
    value: -optimisticUnitValue(input.item.price) * copies,
    newGroups: remainingQuantity === 0 ? -1 : 0,
  };
  const previousFacets =
    queryClient.getQueryData<FacetCountsResponse>(facetKey);
  const nextFacets = previousFacets
    ? applyTotalsDelta(previousFacets, delta)
    : undefined;
  const optimisticFacets =
    nextFacets && nextFacets !== previousFacets
      ? queryClient.setQueryData<FacetCountsResponse>(facetKey, nextFacets)
      : undefined;
  const facetSnapshot =
    previousFacets && optimisticFacets
      ? ({
          previous: previousFacets,
          optimistic: optimisticFacets,
        } satisfies OptimisticCacheSnapshot<FacetCountsResponse>)
      : undefined;

  const previousLists = queryClient.getQueryData<TradeListItemModel[]>(listKey);
  const targetListIndex =
    previousLists?.findIndex((list) => list.id === input.item.list.id) ?? -1;
  const nextLists = previousLists ? [...previousLists] : undefined;
  if (nextLists && targetListIndex >= 0) {
    const list = nextLists[targetListIndex];
    nextLists[targetListIndex] = {
      ...list,
      itemsCount: Math.max(0, list.itemsCount - copies),
    };
  }
  const optimisticLists =
    previousLists && nextLists && targetListIndex >= 0
      ? queryClient.setQueryData<TradeListItemModel[]>(listKey, nextLists)
      : undefined;
  const listSnapshot =
    previousLists && optimisticLists
      ? ({
          previous: previousLists,
          optimistic: optimisticLists,
        } satisfies OptimisticCacheSnapshot<TradeListItemModel[]>)
      : undefined;

  let rolledBack = false;
  return {
    applied: true,
    rollback: () => {
      if (rolledBack) return;
      rolledBack = true;

      queryClient.setQueryData<InfiniteData<BinderItemsPage>>(
        itemKey,
        (value) => rollbackOptimisticCacheSnapshot(value, itemSnapshot)
      );
      if (facetSnapshot) {
        queryClient.setQueryData<FacetCountsResponse>(facetKey, (value) =>
          rollbackOptimisticCacheSnapshot(value, facetSnapshot)
        );
      }
      if (listSnapshot) {
        queryClient.setQueryData<TradeListItemModel[]>(listKey, (lists) =>
          rollbackOptimisticCacheSnapshot(lists, listSnapshot)
        );
      }
      // The owning mutation batch performs one trailing reconciliation. An
      // eager refetch here could overwrite another card's newer optimism.
    },
  };
}
